import React, { useMemo, useEffect } from 'react';
import { View, StyleSheet, ActivityIndicator, AppState } from 'react-native';
import Icon from '@/components/Icon';
import PropTypes from 'prop-types';
import { NeutralThemeProvider, useTheme } from '@/context/ThemeContext';
import { useChatStore } from '@/store';
import useChatWaitingGame from '@/hooks/useChatWaitingGame';
import moment from 'moment-mini';
import Text from '@/components/Typography/Text';
import { useIsFocused } from '@react-navigation/native';

const createStyles = (colors) => StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    minWidth: 0,
    width: '100%',
    paddingVertical: 12,
    paddingHorizontal: 4,
  },
  info: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    width: '100%',
  },
  iconWrapper: {
    width: 18,
    height: 18,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  label: { flex: 1 },
  progressLabel: { fontVariant: ['tabular-nums'] },
  progressBar: {
    height: 2,
    borderRadius: 1,
    overflow: 'hidden',
    width: '100%',
    backgroundColor: colors.solid_color_secondary,
  },
  progressInner: {
    height: '100%',
    borderRadius: 1,
    backgroundColor: colors.text_color_primary,
  }
});

function InferenceJobContent({
  conversationId,
  messageId,
  inferenceId,
  job,
}) {
  const { colors } = useTheme();
  const hasChatMediaPlaceholder = useChatWaitingGame(conversationId, messageId, true);
  const styles = useMemo(() => createStyles(colors), [colors]);
  const isFocused = useIsFocused();
  const [isAppActive, setIsAppActive] = React.useState(AppState.currentState === 'active');

  useEffect(() => {
    const sub = AppState.addEventListener('change', (state) => {
      setIsAppActive(state === 'active');
    });
    return () => {
      sub.remove();
    };
  }, []);
  
  const effectiveStatus = useMemo(() => {
    try {
      const createdAt = job?.createdAt ? moment(job.createdAt) : null;
      const isOverHour = createdAt ? moment().diff(createdAt, 'hours') > 1 : false;
      return (isOverHour ? 'failed' : (job?.status || 'pending')).toLowerCase();
    } catch {
      return (job?.status || 'pending').toLowerCase();
    }
  }, [job?.createdAt, job?.status]);

  const isFailed = useMemo(() => ['failed', 'canceled', 'cancelled'].includes(effectiveStatus), [effectiveStatus]);
  const isCompleted = effectiveStatus === 'completed';
  const isSpinning = !isFailed && !isCompleted;

  // Poll the job status every 3 seconds while active (pending/in_progress/processing).
  // The server caches poll responses for 2 seconds, so polling faster is wasteful.
  // We use a ref-based guard to prevent rapid re-polling when the component
  // re-renders due to addChat() replacing the chat data on each poll response.
  const lastPollTimeRef = React.useRef(0);
  const POLL_INTERVAL_MS = 3000;

  useEffect(() => {
    const hasIds = !!conversationId && !!messageId && !!inferenceId && !!job?.id;
    if (!hasIds || isFailed || isCompleted || !isFocused || !isAppActive) {
      return undefined;
    }

    let isCancelled = false;
    const poll = async () => {
      // Throttle: skip if we polled less than POLL_INTERVAL_MS ago
      const now = Date.now();
      if (now - lastPollTimeRef.current < POLL_INTERVAL_MS - 200) {
        return;
      }
      lastPollTimeRef.current = now;
      
      try {
        await useChatStore.getState().pollInferenceJob(conversationId, messageId, inferenceId, job.id);
      } catch {
        // Silently ignore polling errors
      }
    };

    // Initial poll once when becoming active (throttled)
    poll();

    const intervalId = setInterval(() => {
      if (!isCancelled) poll();
    }, POLL_INTERVAL_MS);

    return () => {
      isCancelled = true;
      clearInterval(intervalId);
    };
  }, [conversationId, messageId, inferenceId, job?.id, isFailed, isCompleted, isFocused, isAppActive]);

  const hasProgress = typeof job?.progress === 'number' && Number.isFinite(job.progress);
  const progress = hasProgress ? Math.max(0, Math.min(100, job.progress)) : 0;

  const jobTypeRaw = job?.type || 'media';
  const jobType = String(jobTypeRaw).toLowerCase();
  const mediaLabel = { audio: 'music', song: 'music', '3d': '3D model' }[jobType] || jobType;
  const prettyJobType = mediaLabel.charAt(0).toUpperCase() + mediaLabel.slice(1);
  const statusText = isFailed
    ? `${prettyJobType} generation ${['canceled', 'cancelled'].includes(effectiveStatus) ? 'cancelled' : 'failed'}`
    : isCompleted
      ? `Your ${mediaLabel} is ready`
      : `Generating your ${mediaLabel}…`;

  // The media card already owns the visible loading state. Keep this component
  // mounted for provider polling, and surface terminal errors here as before.
  if (hasChatMediaPlaceholder && !isFailed && !isCompleted) return null;

  return (
    <View style={styles.container} testID="inference-job-status">
      <View style={styles.info}>
        <View style={styles.headerRow}>
          <View style={styles.titleRow}>
            <View style={styles.iconWrapper}>
              {isSpinning ? (
                <ActivityIndicator size="small" color={colors.text_color_secondary} />
              ) : (
                <Icon
                  name={isFailed ? 'triangle-exclamation' : 'check'}
                  size={14}
                  color={colors.text_color_secondary}
                />
              )}
            </View>
            <Text size="s" color="secondary" style={styles.label}>{statusText}</Text>
            {hasProgress && !isFailed && !isCompleted && <Text size="xs" color="secondary" style={styles.progressLabel}>{Math.round(progress)}%</Text>}
          </View>
        </View>
      {!!job?.error?.message && (
        <Text size='s' color='secondary'>
          {job.error.message}
        </Text>
      )}
      {hasProgress && !isFailed && !isCompleted && (
        <View style={styles.progressBar} accessibilityRole="progressbar" accessibilityValue={{ min: 0, max: 100, now: progress }} accessibilityLabel={statusText}>
          <View style={[styles.progressInner, { width: `${progress}%` }]} />
        </View>
      )}
      </View>
    </View>
  );
}

function MessageInferenceJob(props) {
  return <NeutralThemeProvider><InferenceJobContent {...props} /></NeutralThemeProvider>;
}

MessageInferenceJob.propTypes = {
  conversationId: PropTypes.string.isRequired,
  messageId: PropTypes.string.isRequired,
  inferenceId: PropTypes.string,
  job: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    type: PropTypes.string,
    progress: PropTypes.number,
    updatedAt: PropTypes.string,
    createdAt: PropTypes.string,
    owner: PropTypes.shape({
      entityId: PropTypes.string,
      entityType: PropTypes.string,
    })
  }).isRequired,
  onReload: PropTypes.func,
};

export default React.memo(MessageInferenceJob);
